import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Sanea texto a Latin-1 (jsPDF con fuentes estándar no soporta —, ≥, →, …).
const S = (t) => String(t == null ? '' : t)
  .replace(/[—–]/g, '-').replace(/≥/g, '>=').replace(/≤/g, '<=')
  .replace(/→/g, '->').replace(/…/g, '...').replace(/[^\x00-\xFF]/g, '');

const hexToRgb = (hex) => {
  let h = String(hex || '#4f46e5').replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

/**
 * Genera un informe PDF compacto y prolijo del tablero.
 * `items` es una lista ordenada:
 *   { type:'kpi', label, value, deltaLabel, accent }
 *   { type:'chart', title, accent, node }   // node = elemento DOM a rasterizar
 *   { type:'separador', titulo }
 *   { type:'contenedor', titulo }
 * Las métricas se dibujan nativas (texto vectorial, 3 por fila); los gráficos se
 * insertan como imagen JPEG a lo ancho (varios por página). Archivo liviano y nítido.
 */
export async function exportDashboardPDF(items, { titulo = 'Tablero de KPIs', empresa = 'AlertGuard', logo = null } = {}) {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 10;
  const headerH = 18;
  const gap = 4;
  const contentW = pageW - margin * 2;
  const colW = (contentW - gap * 2) / 3; // 3 métricas por fila
  const kpiH = 23;
  const topStart = headerH + 8;
  const bottom = pageH - margin;
  let pageNum = 0;
  let y = 0;

  const drawHeader = () => {
    pageNum += 1;
    pdf.setFillColor(248, 250, 252);
    pdf.rect(0, 0, pageW, headerH, 'F');
    pdf.setFillColor(79, 70, 229);
    pdf.rect(0, headerH, pageW, 0.8, 'F');
    let tx = margin;
    if (logo) {
      try {
        const fmt = /image\/jpe?g/i.test(logo) ? 'JPEG' : 'PNG';
        pdf.addImage(logo, fmt, margin, 3.5, 11, 11);
        tx = margin + 14;
      } catch (e) { /* logo opcional */ }
    }
    pdf.setTextColor(15, 23, 42); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(12);
    pdf.text(S(empresa), tx, 8);
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); pdf.setTextColor(100, 116, 139);
    pdf.text(`${S(titulo)}  ·  ${S(new Date().toLocaleString('es-AR'))}`, tx, 14);
    pdf.setFontSize(8); pdf.setTextColor(148, 163, 184);
    pdf.text(`Pág. ${pageNum}`, pageW - margin, headerH - 5, { align: 'right' });
    y = topStart;
  };

  const ensure = (h) => { if (y + h > bottom) { pdf.addPage(); drawHeader(); } };

  const oneLine = (txt, maxW, size) => {
    pdf.setFontSize(size);
    let s = S(txt);
    if (pdf.getTextWidth(s) <= maxW) return s;
    while (s.length > 1 && pdf.getTextWidth(`${s}...`) > maxW) s = s.slice(0, -1);
    return `${s}...`;
  };

  const drawKpiCard = (x, cy, w, h, it) => {
    const [r, g, b] = hexToRgb(it.accent);
    pdf.setFillColor(255, 255, 255);
    pdf.setDrawColor(226, 232, 240); pdf.setLineWidth(0.3);
    pdf.roundedRect(x, cy, w, h, 2.2, 2.2, 'FD');
    pdf.setFillColor(r, g, b);
    pdf.roundedRect(x + 2, cy + 2.5, 1.4, h - 5, 0.7, 0.7, 'F');
    const tx = x + 6;
    const innerW = w - 8;
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(7.5); pdf.setTextColor(r, g, b);
    pdf.text(oneLine(it.label, innerW, 7.5), tx, cy + 6.5);
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(14); pdf.setTextColor(15, 23, 42);
    pdf.text(oneLine(it.value, innerW, 14), tx, cy + 15);
    if (it.deltaLabel) {
      pdf.setFont('helvetica', 'normal'); pdf.setFontSize(6.6); pdf.setTextColor(148, 163, 184);
      pdf.text(oneLine(it.deltaLabel, innerW, 6.6), tx, cy + 20);
    }
  };

  // Rasteriza los gráficos (única parte en imagen; JPEG comprimido).
  for (const it of items) {
    if (it.type === 'chart' && it.node) {
      it._canvas = await html2canvas(it.node, { scale: 2, backgroundColor: '#ffffff', useCORS: true, logging: false });
    }
  }

  drawHeader();

  // Buffer de métricas para empaquetarlas de a 3.
  let kbuf = [];
  const flushKpis = () => {
    if (!kbuf.length) return;
    for (let i = 0; i < kbuf.length; i += 3) {
      const row = kbuf.slice(i, i + 3);
      ensure(kpiH);
      row.forEach((it, k) => drawKpiCard(margin + k * (colW + gap), y, colW, kpiH, it));
      y += kpiH + gap;
    }
    kbuf = [];
  };

  const drawSeparador = (titulo) => {
    flushKpis();
    const bandH = 11;
    ensure(bandH);
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9); pdf.setTextColor(71, 85, 105);
    const t = S(titulo || 'Sección').toUpperCase();
    pdf.text(t, margin, y + 5);
    const tw = pdf.getTextWidth(t);
    pdf.setDrawColor(203, 213, 225); pdf.setLineWidth(0.3);
    pdf.line(margin + tw + 3, y + 4, pageW - margin, y + 4);
    y += bandH + gap;
  };

  const drawContenedor = (titulo) => {
    flushKpis();
    const bandH = 13;
    ensure(bandH);
    pdf.setFillColor(238, 242, 255);
    pdf.roundedRect(margin, y, contentW, bandH - 2, 2, 2, 'F');
    pdf.setFillColor(79, 70, 229);
    pdf.roundedRect(margin + 2.5, y + 2, 1.6, bandH - 6, 0.8, 0.8, 'F');
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11.5); pdf.setTextColor(30, 41, 59);
    pdf.text(oneLine(titulo || 'Grupo', contentW - 12, 11.5), margin + 7, y + 7.5);
    y += bandH + gap;
  };

  const drawChart = (it) => {
    flushKpis();
    const c = it._canvas;
    if (!c) return;
    let w = contentW;
    let h = (c.height * w) / c.width;
    const maxH = bottom - topStart;
    if (h > maxH) { h = maxH; w = (c.width * h) / c.height; }
    ensure(h);
    const x = margin + (contentW - w) / 2;
    pdf.addImage(c.toDataURL('image/jpeg', 0.82), 'JPEG', x, y, w, h);
    y += h + gap;
  };

  for (const it of items) {
    if (it.type === 'kpi') kbuf.push(it);
    else if (it.type === 'separador') drawSeparador(it.titulo);
    else if (it.type === 'contenedor') drawContenedor(it.titulo);
    else if (it.type === 'chart') drawChart(it);
  }
  flushKpis();

  pdf.save(`${S(titulo).replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`);
}

/**
 * Exporta el PDF de UNA sección: descubre automáticamente las métricas (StatCards)
 * y los gráficos dentro de `rootEl` y los arma en un informe compacto (métricas 3
 * por fila, gráficos a lo ancho). Todo como imagen JPEG comprimida.
 */
export async function exportSectionPDF(rootEl, { title = 'Sección', empresa = 'AlertGuard', logo = null } = {}) {
  if (!rootEl) throw new Error('Elemento no encontrado');

  const kpiNodes = Array.from(rootEl.querySelectorAll('[data-testid*="kpi-"]'));
  const chartWraps = [];
  const seen = new Set();
  rootEl.querySelectorAll('.recharts-responsive-container').forEach((rc) => {
    if (rc.clientHeight < 120) return; // excluye sparklines de las StatCards
    const wrap = rc.closest('[data-testid]');
    if (!wrap || seen.has(wrap)) return;
    if ((wrap.getAttribute('data-testid') || '').includes('kpi-')) return;
    seen.add(wrap);
    chartWraps.push(wrap);
  });

  const items = [
    ...kpiNodes.map((n) => ({ n, span: 1 })),
    ...chartWraps.map((n) => ({ n, span: 2 })),
  ];
  items.sort((a, b) => ((a.n.compareDocumentPosition(b.n) & Node.DOCUMENT_POSITION_FOLLOWING) ? -1 : 1));

  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 10, headerH = 18, gap = 4;
  const contentW = pageW - margin * 2;
  const colW = (contentW - gap * 2) / 3;
  const topStart = headerH + 8, bottom = pageH - margin;
  let pageNum = 0, y = 0;

  const drawHeader = () => {
    pageNum += 1;
    pdf.setFillColor(248, 250, 252);
    pdf.rect(0, 0, pageW, headerH, 'F');
    pdf.setFillColor(79, 70, 229);
    pdf.rect(0, headerH, pageW, 0.8, 'F');
    let tx = margin;
    if (logo) {
      try { pdf.addImage(logo, /image\/jpe?g/i.test(logo) ? 'JPEG' : 'PNG', margin, 3.5, 11, 11); tx = margin + 14; } catch (e) { /* opcional */ }
    }
    pdf.setTextColor(15, 23, 42); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(12);
    pdf.text(S(empresa), tx, 8);
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); pdf.setTextColor(100, 116, 139);
    pdf.text(`KPIs · ${S(title)}  ·  ${S(new Date().toLocaleString('es-AR'))}`, tx, 14);
    pdf.setFontSize(8); pdf.setTextColor(148, 163, 184);
    pdf.text(`Pág. ${pageNum}`, pageW - margin, headerH - 5, { align: 'right' });
    y = topStart;
  };
  const ensure = (h) => { if (y + h > bottom) { pdf.addPage(); drawHeader(); } };

  for (const it of items) {
    it._canvas = await html2canvas(it.n, { scale: 2, backgroundColor: '#ffffff', useCORS: true, logging: false });
  }

  drawHeader();
  // Título de la sección
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(15); pdf.setTextColor(15, 23, 42);
  pdf.text(S(title), margin, y + 4);
  y += 10;

  const addImg = (canvas, x, w) => {
    const h = (canvas.height * w) / canvas.width;
    pdf.addImage(canvas.toDataURL('image/jpeg', 0.82), 'JPEG', x, y, w, h);
    return h;
  };

  let kbuf = [];
  const flushKpis = () => {
    if (!kbuf.length) return;
    for (let i = 0; i < kbuf.length; i += 3) {
      const row = kbuf.slice(i, i + 3);
      const rowH = Math.max(...row.map((c) => (c.height * colW) / c.width));
      ensure(rowH);
      row.forEach((c, k) => addImg(c, margin + k * (colW + gap), colW));
      y += rowH + gap;
    }
    kbuf = [];
  };

  const maxContentH = bottom - topStart;
  for (const it of items) {
    if (it.span === 1) { kbuf.push(it._canvas); continue; }
    flushKpis();
    const c = it._canvas;
    let w = contentW, h = (c.height * w) / c.width;
    if (h > maxContentH) { h = maxContentH; w = (c.width * h) / c.height; }
    ensure(h);
    addImg(c, margin + (contentW - w) / 2, w);
    y += h + gap;
  }
  flushKpis();

  pdf.save(`KPIs_${S(title).replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`);
}
